<script setup>
import { ref, computed } from 'vue'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { AlertCircle, SendHorizontal } from 'lucide-vue-next'
import { useChatConfig } from '@/composables/useChatConfig'
import ModelSelector from './ModelSelector.vue'
import ModeSelector from './ModeSelector.vue'

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: 'Type your message...'
  },
  modelValue: {
    type: String,
    required: true
  },
  orgId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['send', 'update:modelValue'])

const message = ref('')
const inputMode = ref('chat') // 'chat' or 'report'

const isReportMode = computed(() => inputMode.value === 'report')

// Get chat configuration (loaded from front-config)
const { maxChars, validateMessage } = useChatConfig()

// Character count and validation
const charCount = computed(() => message.value.trim().length)
const validation = computed(() => validateMessage(message.value))

const isValid = computed(() => validation.value.isValid)
const isNearLimit = computed(() => charCount.value > maxChars.value * 0.8)
const isOverLimit = computed(() => charCount.value > maxChars.value)
const showCounter = computed(() => charCount.value > 3500)

const counterClass = computed(() => {
  if (isOverLimit.value) return 'text-red-600 font-semibold'
  if (isNearLimit.value) return 'text-orange-500 font-medium'
  return 'text-muted-foreground'
})

const ariaDescribedBy = computed(() => {
  const ids = ['chat-input-counter']
  if (!isValid.value && charCount.value > 0) {
    ids.push('chat-input-error')
  }
  return ids.join(' ')
})

const sendButtonLabel = computed(() => {
  return `Send message${!isValid.value ? ' (message invalid)' : ''}`
})

const handleSend = () => {
  if (message.value.trim() && !props.disabled && isValid.value) {
    let content = message.value.trim()
    if (isReportMode.value) {
      content = `/report ${content}`
    }
    emit('send', content)
    message.value = ''
    // Mode stays persistent - user must manually switch back to Chat
  }
}

const handleKeydown = (event) => {
  // Send on Enter (without Shift)
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSend()
  }
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex gap-3 items-end">
      <div class="flex-1 relative">
        <Textarea
          v-model="message"
          :placeholder="placeholder"
          :disabled="disabled"
          @keydown="handleKeydown"
          :class="[
            'min-h-[60px] max-h-[200px] resize-none rounded-lg',
            { 'border-red-500 focus-visible:ring-red-500': isOverLimit }
          ]"
          :aria-describedby="ariaDescribedBy"
          :aria-invalid="!isValid && charCount > 0"
          rows="2"
        />
      </div>
    </div>

    <div class="flex justify-between space-x-2">
      <div class="flex space-x-2 items-center">
        <ModelSelector
          :model-value="modelValue"
          @update:model-value="emit('update:modelValue', $event)"
          :disabled="disabled"
          :org-id="orgId"
        />
      </div>

      <div class="flex items-center space-x-2">
        <div>
          <ModeSelector
            v-model="inputMode"
            :disabled="disabled"
          />
        </div>
        <Button
          @click="handleSend"
          :disabled="disabled || !message.trim() || !isValid"
          :aria-label="sendButtonLabel"
          class="w-10 h-10 p-0"
        >
          <SendHorizontal class="h-5.5 w-5.5 text-white" />
        </Button>
      </div>

    </div>

    <!-- Character counter - only show when > 3500 or validation error -->
    <div v-if="showCounter || (!isValid && charCount > 0)" class="flex items-center justify-between text-sm">
      <div class="flex items-center gap-2">
        <!-- Counter -->
        <span
          v-if="showCounter"
          id="chat-input-counter"
          :class="counterClass"
          aria-live="polite"
          aria-atomic="true"
        >
          {{ charCount }} / {{ maxChars }} characters
        </span>

        <!-- Validation error message -->
        <div
          v-if="!isValid && charCount > 0"
          id="chat-input-error"
          class="flex items-center gap-1 text-red-600"
          role="alert"
          aria-live="assertive"
        >
          <AlertCircle class="h-4 w-4" aria-hidden="true" />
          <span>{{ validation.error }}</span>
        </div>
      </div>
    </div>

    <p class="m-0 text-xs text-muted-foreground">
      Assistant can make mistakes. Please double-check responses.
    </p>
  </div>
</template>
